import { Volunteer, CheckInStatus, Task, PasscodeValidation, ApiResponse, Notification, NotificationStats } from '../types';

// Use environment variable or default to relative path for production (nginx proxy)
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Get auth headers for admin endpoints
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('adminToken');
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
}

// Generic API call function
async function apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Authenticated API call for admin endpoints
async function authApiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Authentication API calls
export const authApi = {
  validatePasscode: (code: string): Promise<PasscodeValidation> =>
    apiCall<PasscodeValidation>('/validate-passcode', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

// Volunteer API calls
export const volunteerApi = {
  getAll: (): Promise<Volunteer[]> =>
    apiCall<Volunteer[]>('/volunteers'),

  getById: (id: number): Promise<Volunteer> =>
    apiCall<Volunteer>(`/volunteers/${id}`),

  create: (volunteer: Omit<Volunteer, 'id' | 'createdAt' | 'passcode'>): Promise<ApiResponse<Volunteer>> =>
    apiCall<ApiResponse<Volunteer>>('/volunteers', {
      method: 'POST',
      body: JSON.stringify(volunteer),
    }),

  update: (id: number, volunteer: Partial<Volunteer>): Promise<ApiResponse<Volunteer>> =>
    apiCall<ApiResponse<Volunteer>>(`/volunteers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(volunteer),
    }),

  delete: (id: number): Promise<ApiResponse> =>
    apiCall<ApiResponse>(`/volunteers/${id}`, {
      method: 'DELETE',
    }),

  toggleStatus: (id: number, isActive: boolean): Promise<ApiResponse> =>
    apiCall<ApiResponse>(`/volunteers/${id}/toggle-status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    }),
};

// Check-in API calls
export const checkInApi = {
  getStatus: (passcode: string): Promise<CheckInStatus> =>
    apiCall<CheckInStatus>(`/checkin-status/${passcode}`),

  checkIn: (passcode: string, taskId: number): Promise<ApiResponse<{ checkInTime: string }>> =>
    apiCall<ApiResponse<{ checkInTime: string }>>('/checkin', {
      method: 'POST',
      body: JSON.stringify({ passcode, task_id: taskId }),
    }),

  checkOut: (passcode: string): Promise<ApiResponse<{ checkOutTime: string }>> =>
    apiCall<ApiResponse<{ checkOutTime: string }>>('/checkout', {
      method: 'POST',
      body: JSON.stringify({ code: passcode }),
    }),
};

// Tasks API calls
export const taskApi = {
  getAll: (): Promise<Task[]> =>
    apiCall<Task[]>('/tasks'),
};

// Liability waiver API calls (requires auth)
export const liabilityWaiverApi = {
  getAll: (): Promise<Volunteer[]> =>
    authApiCall<Volunteer[]>('/liability-waivers'),

  getExpired: (): Promise<Volunteer[]> =>
    authApiCall<Volunteer[]>('/liability-waivers/expired'),
};

// Notification API calls (requires auth)
export const notificationApi = {
  getAll: (params?: { type?: string; limit?: string; offset?: string }): Promise<{ success: boolean; data: Notification[]; message: string }> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.limit) queryParams.append('limit', params.limit);
    if (params?.offset) queryParams.append('offset', params.offset);
    
    const endpoint = queryParams.toString() ? `/notifications?${queryParams.toString()}` : '/notifications';
    return authApiCall<{ success: boolean; data: Notification[]; message: string }>(endpoint);
  },

  getStats: (): Promise<{ success: boolean; data: NotificationStats }> =>
    authApiCall<{ success: boolean; data: NotificationStats }>('/notifications/stats'),
};
